/**
 * @description 页面级埋点 composable：PV、停留时长、滚动深度
 * @reason UV 指标由后台根据 PV 按用户去重计算，前端仅上报 PV
 */

import { onBeforeUnmount, onMounted, toValue, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useThrottleFn } from '@/composables/useDebounceThrottle'
import {
  buildScrollDepthProperties,
  calcScrollPercent,
  getNewlyReachedScrollThresholds,
} from '~/utils/tracking/scrollDepth'
import {
  buildProductTrackFields,
  buildSchemeTrackFields,
  toPvEventName,
} from '~/utils/tracking/helpers'
import {
  clearPageExposure,
  flushTrackQueue,
  hasExposedInPage,
  markExposedInPage,
  safeTrack,
  usePageTimer,
} from '~/utils/tracker'

/**
 * @description 页面 PV / 时长 / 滚动深度埋点
 * @param {Object|Function|import('vue').Ref|import('vue').ComputedRef} optionsSource
 * @param {Function} [optionsSource.ready] - 返回 true 后再上报 PV（商品详情等需等型号就绪）
 * @param {Object} [optionsSource.product] - 商品对象，用于 enrich model_no
 * @param {Object} [optionsSource.item] - 列表项对象，用于 enrich model_no
 * @returns {{ trackPageView: Function, trackDuration: Function }}
 */
export function usePageTracking(optionsSource) {
  const resolveOptions = () => toValue(optionsSource)

  const pageTimer = usePageTimer()
  const reachedDepths = new Set()
  let scrollHandler = null
  /** 最近一次解析出的埋点字段 */
  let activeEvents = null
  /** PV 成功时锁定的时长上下文，避免离页时 route 已变导致缺商品/方案维度 */
  let durationContext = null
  let stopReadyWatch = null
  let removePageLifecycleListeners = null

  const getResolvedEvents = () => {
    const options = resolveOptions()
    const product = options.product
    const item = options.item
    const productFields = product || item ? buildProductTrackFields(product || item) : null
    const schemeFields =
      !productFields?.targetName && (options.scheme || item?.solutionName || item?.solutionId)
        ? buildSchemeTrackFields(options.scheme || item)
        : null
    const explicitTargetName = String(options.targetName || '').trim()
    const productTargetName =
      productFields?.targetName ||
      productFields?.modelNo ||
      String(options.properties?.prod_name || '').trim() ||
      String(options.properties?.model_no || '').trim()
    const schemeTargetName =
      schemeFields?.targetName ||
      String(options.properties?.scheme_name || '').trim() ||
      String(options.properties?.case_name || '').trim() ||
      String(options.properties?.category_name || '').trim()
    // 商品/方案/案例实体页：禁止用 pageName（如「方案详情页」）占位目标名称
    const allowPageNameFallback =
      !product &&
      !item &&
      !options.scheme &&
      options.targetType !== 'product_detail' &&
      options.targetType !== 'scheme_detail' &&
      options.targetType !== 'case_detail' &&
      !String(options.pvEvent || '').includes('ProductDetailPage') &&
      !String(options.pvEvent || '').includes('SchemeDetailPage') &&
      !String(options.pvEvent || '').includes('CaseDetailPage')
    const targetName =
      explicitTargetName ||
      productTargetName ||
      schemeTargetName ||
      (allowPageNameFallback ? String(options.pageName || '').trim() : '')

    const resolved = {
      pageKey: options.pageKey,
      pvEvent: options.pvEvent,
      pageName: options.pageName,
      targetType: options.targetType,
      targetId: options.targetId ?? options.pageKey,
      targetName,
      durationPvEvent: options.durationPvEvent || `${options.pvEvent}_Duration`,
      scrollDepthPvEvent: options.scrollDepthPvEvent || `${options.pvEvent}_Scroll_Depth`,
      enableScrollDepth: options.enableScrollDepth !== false,
      enableDuration: options.enableDuration !== false,
      properties: options.properties || {},
      product,
      item,
      scheme: options.scheme || schemeFields?.scheme,
      sourceType: options.sourceType,
      sourceId: options.sourceId,
      sourceName: options.sourceName,
      ready: options.ready,
    }
    activeEvents = resolved
    return resolved
  }

  const trackPageView = () => {
    if (!import.meta.client) return
    const events = getResolvedEvents()
    if (hasExposedInPage(events.pageKey)) {
      // 同页重入 / HMR：PV 已去重，仍需补齐时长上下文
      if (!durationContext && events.enableDuration) {
        durationContext = {
          ...events,
          properties: { ...(events.properties || {}) },
        }
        pageTimer.beginTiming({ force: true })
      }
      return
    }
    markExposedInPage(events.pageKey)
    // 锁定本页时长维度（深拷贝 properties），并从 PV 时刻重新计时
    durationContext = {
      ...events,
      properties: { ...(events.properties || {}) },
    }
    pageTimer.beginTiming({ force: true })
    safeTrack({
      eventName: events.pvEvent,
      eventType: 'impression',
      targetType: events.targetType,
      targetId: String(events.targetId),
      targetName: events.targetName,
      pageName: events.pageName,
      product: events.product,
      item: events.item || events.product || events.scheme,
      scheme: events.scheme,
      sourceType: events.sourceType,
      sourceId: events.sourceId,
      sourceName: events.sourceName,
      properties: { ...events.properties },
    })
  }

  /**
   * @description 在 ready 条件满足时上报页面 PV；未就绪则返回 false 供外部继续等待
   * @returns {boolean}
   */
  const tryTrackPageViewWhenReady = () => {
    const events = getResolvedEvents()
    if (typeof events.ready === 'function' && !events.ready()) return false
    trackPageView()
    return true
  }

  /**
   * @description 上报停留时长（可传入上一页快照，用于 SPA 同组件切实体时结算）
   * @param {Object} [eventsSnapshot]
   * @returns {void}
   */
  const trackDuration = (eventsSnapshot) => {
    if (!import.meta.client) return
    const events = eventsSnapshot || durationContext || activeEvents || getResolvedEvents()
    if (!events?.enableDuration) {
      pageTimer.endTiming()
      return
    }
    const isEntityDetail =
      events.targetType === 'product_detail' ||
      events.targetType === 'scheme_detail' ||
      events.targetType === 'case_detail'
    // 实体详情：必须已成功 PV（有锁定上下文或曝光标记），否则不上报空维度停留
    if (
      isEntityDetail &&
      !eventsSnapshot &&
      !durationContext &&
      !hasExposedInPage(events.pageKey)
    ) {
      pageTimer.endTiming()
      return
    }
    const dur = pageTimer.endTiming()
    if (dur <= 0) return

    const targetId = String(
      events.targetId ||
        events.properties?.scheme_id ||
        events.properties?.prod_id ||
        events.properties?.case_id ||
        events.pageKey ||
        ''
    )
    const targetName = String(
      events.targetName ||
        events.properties?.scheme_name ||
        events.properties?.prod_name ||
        events.properties?.case_name ||
        ''
    ).trim()
    if (!targetId) return

    safeTrack({
      eventName: events.durationPvEvent,
      eventType: 'timing',
      targetType: events.targetType,
      targetId,
      targetName,
      pageName: events.pageName,
      // 时长事件不再附带完整 product/scheme 大对象，维度走 properties
      sourceType: events.sourceType,
      sourceId: events.sourceId,
      sourceName: events.sourceName,
      properties: {
        ...(events.properties || {}),
        // 放最后，避免被业务 properties 覆盖；兼容监控「数值」列常见字段
        total_duration_ms: dur,
        duration: dur,
        value: dur,
        avg_per_session: true,
      },
    })
  }

  /**
   * @description 结算当前段停留；切实体后等新 PV 再重启计时
   * @param {Object} [eventsSnapshot]
   * @param {{ restart?: boolean }} [options]
   * @returns {void}
   */
  const settleDurationAndRestart = (eventsSnapshot, options = {}) => {
    trackDuration(eventsSnapshot)
    durationContext = null
    if (options.restart !== false) {
      pageTimer.beginTiming({ force: true })
    }
  }

  const trackScrollDepth = (depthPercent) => {
    const events = getResolvedEvents()
    const depthValue = Number(depthPercent)
    const depthKey = `${events.pageKey}_scroll_${depthValue}`
    if (reachedDepths.has(depthValue) || hasExposedInPage(depthKey)) return
    reachedDepths.add(depthValue)
    markExposedInPage(depthKey)

    safeTrack({
      eventName: events.scrollDepthPvEvent,
      eventType: 'impression',
      targetType: events.targetType,
      targetId: String(events.targetId),
      targetName: events.targetName,
      pageName: events.pageName,
      product: events.product,
      item: events.item || events.product,
      sourceType: events.sourceType,
      sourceId: events.sourceId,
      sourceName: events.sourceName,
      scroll_depth: depthValue,
      properties: buildScrollDepthProperties(depthValue, {
        ...events.properties,
        avg_per_session: true,
      }),
    })
  }

  /**
   * @description 按当前滚动百分比升序补报未达档位（25→50→75→100）
   * @param {number} percent
   * @param {{ shortPage?: boolean }} [options]
   * @returns {void}
   */
  const trackScrollDepthByPercent = (percent, options = {}) => {
    getNewlyReachedScrollThresholds(percent, reachedDepths, options).forEach((threshold) => {
      trackScrollDepth(threshold)
    })
  }

  const handleScroll = useThrottleFn(() => {
    if (!import.meta.client) return
    const events = getResolvedEvents()
    if (!events.enableScrollDepth) return
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight
    const shortPage = !(Number.isFinite(maxScroll) && maxScroll > 0)
    const percent = calcScrollPercent(scrollTop, maxScroll)
    trackScrollDepthByPercent(percent, { shortPage })
  }, 200)

  onMounted(() => {
    if (!import.meta.client) return
    // 监听 pageKey/ready：详情页异步数据可延迟上报；SPA 切实体时结算上一段时长
    stopReadyWatch = watch(
      () => {
        const options = resolveOptions()
        return {
          pageKey: options.pageKey,
          ready: typeof options.ready === 'function' ? Boolean(options.ready()) : true,
          targetName: String(options.targetName || '').trim(),
          targetId: String(options.targetId ?? options.pageKey ?? ''),
        }
      },
      (curr, prev) => {
        if (prev?.pageKey && curr.pageKey && prev.pageKey !== curr.pageKey) {
          // 优先用 PV 锁定快照结算，切实体后等新 PV 再计时
          settleDurationAndRestart(durationContext || activeEvents || undefined, {
            restart: false,
          })
          reachedDepths.clear()
        }
        getResolvedEvents()
        if (!curr.ready) return
        tryTrackPageViewWhenReady()
      },
      { immediate: true }
    )

    const events = getResolvedEvents()
    // 无 ready 门槛的页面：若 immediate 已 PV 则已在计时；否则兜底开始
    if (!durationContext && events.enableDuration) {
      pageTimer.beginTiming({ force: true })
    }
    if (events.enableScrollDepth) {
      scrollHandler = handleScroll
      window.addEventListener('scroll', scrollHandler, { passive: true })
      handleScroll()
    }

    // 关页/刷新/切后台：补报停留
    const handlePageHide = () => {
      trackDuration(durationContext || activeEvents || undefined)
      flushTrackQueue()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handlePageHide()
        return
      }
      // 仅在已 PV 后回前台续计，避免商详未就绪段被计入
      if (durationContext) pageTimer.beginTiming({ force: true })
    }
    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    removePageLifecycleListeners = () => {
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  })

  // SPA 路由离开时先结算停留（早于 DOM 卸载）
  onBeforeRouteLeave(() => {
    if (!import.meta.client) return
    trackDuration(durationContext || activeEvents || undefined)
    durationContext = null
    flushTrackQueue()
  })

  onBeforeUnmount(() => {
    if (!import.meta.client) return
    removePageLifecycleListeners?.()
    removePageLifecycleListeners = null
    stopReadyWatch?.()
    stopReadyWatch = null
    trackDuration(durationContext || activeEvents || undefined)
    durationContext = null
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler)
    }
    flushTrackQueue()
    clearPageExposure()
  })

  return { trackPageView, trackDuration }
}

/**
 * @description Banner 曝光/点击埋点辅助
 * @param {Object} config
 * @returns {{ trackBannerExposure: Function, trackBannerClick: Function }}
 */
export function useBannerTracking(config) {
  const { pvEvent, clickEvent, pageName, targetType = 'homepage_banner' } = config

  const trackBannerExposure = (banner, index) => {
    const bannerId = String(banner?.id ?? banner?.bannerId ?? index)
    const exposureKey = `${pvEvent}_${bannerId}_${index}`
    if (hasExposedInPage(exposureKey)) return
    markExposedInPage(exposureKey)

    safeTrack({
      eventName: pvEvent,
      eventType: 'impression',
      targetType,
      targetId: bannerId,
      targetName: banner?.title || banner?.mainTitle || '',
      targetIndex: index,
      pageName,
      properties: {
        banner_index: index,
        banner_id: bannerId,
        banner_name: banner?.title || banner?.mainTitle || '',
      },
    })
  }

  const trackBannerClick = (banner, index) => {
    const bannerId = String(banner?.id ?? banner?.bannerId ?? index)
    safeTrack({
      eventName: clickEvent,
      eventType: 'custom_click',
      targetType,
      targetId: bannerId,
      targetName: banner?.title || banner?.mainTitle || '',
      targetIndex: index,
      pageName,
      properties: {
        banner_index: index,
        banner_id: bannerId,
        banner_name: banner?.title || banner?.mainTitle || '',
      },
    })
  }

  return { trackBannerExposure, trackBannerClick }
}

/**
 * @description 卡片曝光/点击埋点辅助
 * @param {Object} config
 * @returns {{ trackCardExposure: Function, trackCardClick: Function }}
 */
export function useCardTracking(config) {
  const { pvEvent, clickEvent, pageName, targetType = 'homepage_card', sourceType, sourceId, sourceName } =
    config

  const trackCardExposure = (fields, extra = {}) => {
    const exposureKey = `${pvEvent}_${fields.targetId}`
    if (hasExposedInPage(exposureKey)) return
    markExposedInPage(exposureKey)

    safeTrack({
      eventName: pvEvent,
      eventType: 'impression',
      targetType,
      targetId: fields.targetId,
      targetName: fields.targetName,
      pageName,
      product: fields.product,
      item: fields.product || fields.scheme,
      scheme: fields.scheme,
      sourceType: sourceType || fields.sourceType,
      sourceId: sourceId || fields.sourceId,
      sourceName: sourceName || fields.sourceName,
      properties: { ...fields.properties, ...extra },
    })
  }

  const trackCardClick = (fields, extra = {}) => {
    safeTrack({
      eventName: clickEvent,
      eventType: 'custom_click',
      targetType,
      targetId: fields.targetId,
      targetName: fields.targetName,
      pageName,
      product: fields.product,
      item: fields.product || fields.scheme,
      scheme: fields.scheme,
      sourceType: sourceType || fields.sourceType,
      sourceId: sourceId || fields.sourceId,
      sourceName: sourceName || fields.sourceName,
      properties: { ...fields.properties, ...extra },
    })
  }

  return { trackCardExposure, trackCardClick }
}

/**
 * @description 通用点击埋点（仅 PV，UV 由后台去重）
 * @param {Object} payload
 * @returns {void}
 */
export function trackClickEvent(payload) {
  const {
    pvEvent,
    targetType,
    targetId,
    targetName,
    pageName,
    properties = {},
    product,
    item,
    sourceType,
    sourceId,
    sourceName,
  } = payload

  safeTrack({
    eventName: toPvEventName(pvEvent),
    eventType: 'custom_click',
    targetType,
    targetId: String(targetId),
    targetName,
    pageName,
    product: product || item,
    item: item || product,
    sourceType,
    sourceId,
    sourceName,
    properties,
  })
}
